import { useWidget } from '@telescope/cassini-hooks';
import { Box, Button, VStack, FormControl, Text, useColorModeValue, Divider } from '@telescope/cassini-ui';
import { GenericObject } from '@telescope/cassini-utilities';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAuth } from '../lib';
import { AuthMethod, InputName, renderInput } from '../utils';
import { FacebookButton } from './FacebookButton';
import { ErrorWarning } from './ErrorWarning';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils';
import { Open, Closed } from 'generated-types/widget';
interface RegisterFormStep1Props {
  onFormSubmit(formData: GenericObject): void;
}
export const RegisterFormStep1 = ({ onFormSubmit }: RegisterFormStep1Props) => {
  const { isRegistering } = useAuth();
  const { data } = useWidget({ select: (data: Open) =>  data.widget.global_views.auth });
  const { content: { registrationModal: content } } = data!;
  const formMethods = useForm<{ [x: string]: any }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });
  const { handleSubmit, setValue, watch, register, getValues, trigger, setError, formState: { errors } } = formMethods;

  const { isMethodEnabled, authMethods } = useAuth();
  const hasMultipleMethods = Object.values(authMethods).filter((method) => method).length > 1;
  const watchMethod = watch('method');
  const hasError = !isEmpty(errors);

  const dividerColor = useColorModeValue('gray.800', 'gray.400');

  const navigate = useNavigate();

  // Save the authMethod as a form value
  useEffect(() => {
    register('method');
  }, [register]);

  const handleContinueClick = async (method: AuthMethod) => {
    // If no email is entered and method is email:
    // Trigger all fields to check for other errors
    // Set email error
    if (method === AuthMethod.EMAIL && !getValues(InputName.EMAIL)) {
      await trigger();
      return setError(InputName.EMAIL, { type: 'required', message: content.emailInput.errorMessage });
    }

    setValue('method', method);
    handleSubmit(onFormSubmit)();
  };

  return (
    <FormProvider {...formMethods}>
      <FormControl isInvalid={!!hasError}>
        <ErrorWarning>
          {content.errorMessage.generic}
        </ErrorWarning>
      </FormControl>

      <form onSubmit={(e) => e.preventDefault()}>
        <VStack spacing={5} padding={[5,5,0]}>
          {/* Email */}
          {isMethodEnabled(AuthMethod.EMAIL) && (
            <Box w="100%">
              {renderInput(content.emailInput)}
              <Button
                isLoading={watchMethod === AuthMethod.EMAIL && isRegistering}
                isDisabled={isRegistering}
                width="full"
                type="button"
                mt={3}
                onClick={() => handleContinueClick(AuthMethod.EMAIL)}>
                {content.buttons.email}
              </Button>
            </Box>
          )}

          { hasMultipleMethods && isMethodEnabled(AuthMethod.EMAIL) && 
            <Text fontSize="sm" whiteSpace="nowrap" color={dividerColor} textAlign="center">
              {content.dividerText}
            </Text> }
        
          {/* Facebook */}
          {isMethodEnabled(AuthMethod.FACEBOOK) && (
            <FacebookButton
              isLoading={watchMethod === AuthMethod.FACEBOOK && isRegistering}
              isDisabled={isRegistering}
              onClick={() => handleContinueClick(AuthMethod.FACEBOOK)}>
              {content.buttons.facebook}
            </FacebookButton>
          )}

          {/* Custom SSO */}
          {isMethodEnabled(AuthMethod.OAUTH) && <>{/* TODO: OAuth */}</>}

          { hasMultipleMethods && isMethodEnabled(AuthMethod.EMAIL) && 
              <Divider borderColor={dividerColor}/> }

          <Button
            size="md"
            type="button"
            variant="link"
            onClick={() => navigate(ROUTES.LOGIN, { replace: true, state: { backgroundLocation: ROUTES.HOME } })}>
            {content.loginLink}
          </Button>

        </VStack>
      </form>
    </FormProvider>
  );
};
